import { useNavigate } from '@solidjs/router'
import { Show, createResource } from 'solid-js'
import { describeCacheAge, loadHub } from '../../lib/hub'
import { isOnline, readQueue } from '../../lib/pwa'
import ActiveCheckInCard from './ActiveCheckInCard'
import BalanceCard from './BalanceCard'
import HubShell from './HubShell'
import OfflineBanner from './OfflineBanner'
import PassCard from './PassCard'
import RecentHistoryCard from './RecentHistoryCard'
import VenueActionCard from './VenueActionCard'

/**
 * The post-signup hub — the guest's home screen.
 *
 * Composition only: it owns the load, the refresh, and the navigation targets,
 * and delegates every visual unit to a card. Keeping the data in H1 (`loadHub`)
 * and the presentation in components means a card can be restyled or tested
 * without touching the fetch logic.
 */
export default function HubView(props: {
  onTopUp: () => void
  onCheckIn: () => void
  onSignOut: () => void
  /** True when the flow just queued an offline check-in — surfaced so the guest
   *  sees it was saved rather than lost. */
  offlineSaved?: boolean
}) {
  const navigate = useNavigate()
  const [hub, { refetch }] = createResource(loadHub)

  const state = () => hub()?.state ?? null

  return (
    <HubShell onSignOut={() => props.onSignOut()}>
      <Show when={hub.loading}>
        <p class="text-sm text-white/50 py-8 text-center">Loading your ZipPass…</p>
      </Show>

      <Show when={!hub.loading && hub()?.error}>
        <div class="rounded-2xl bg-red-500/10 border border-red-400/30 p-5 text-center">
          <p class="text-sm text-red-200">{hub()?.error}</p>
          <button
            type="button"
            onClick={() => void refetch()}
            class="mt-3 px-4 py-2 rounded-lg bg-white/10 text-white text-sm hover:bg-white/15"
          >
            Try again
          </button>
        </div>
      </Show>

      <Show when={state()} keyed>
        {(s) => (
          <>
            <OfflineBanner
              online={isOnline()}
              queuedCount={readQueue().length}
              cacheAge={hub()?.fromCache ? describeCacheAge(s.loadedAt) : null}
              savedLocally={props.offlineSaved}
            />

            <BalanceCard balanceCents={s.balance} onTopUp={() => props.onTopUp()} />

            {/* The arrival/venue-link state sits directly under the balance:
                it is the hub's answer to "where am I right now?". */}
            <VenueActionCard activeCheckIns={s.activeCheckIns} onCheckIn={() => props.onCheckIn()} />

            <Show when={s.activeCheckIns.length > 0}>
              <ActiveCheckInCard activeCheckIns={s.activeCheckIns} />
            </Show>

            <PassCard passId={s.passId} addToWalletUrl={s.addToWalletUrl} qrPayload={s.qrPayload} />

            <RecentHistoryCard recentHistory={s.recentHistory} onSeeAll={() => navigate('/history')} />
          </>
        )}
      </Show>
    </HubShell>
  )
}
